<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1200px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group horizontal name="sort" :label="$t('attributes.sort')">
              <b-select v-model="form.sort" name="sort">
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>

            <form-group horizontal name="topic" :label="$t('attributes.topic')">
              <b-input
                v-model="form.topic"
                name="topic"
                :placeholder="
                  $t('message.please_input') + $t('attributes.topic')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="area_code"
              attribute="attributes.area"
              :label="$t('attributes.area')"
            >
              <b-select
                v-model="selectedArea"
                name="area_code"
                :loading="loadingAreas"
                :placeholder="
                  $t('message.please_select') + $t('attributes.area')
                "
                expanded
                @input="onSelectArea"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.area') }}
                </option>
                <option
                  v-for="option in areas"
                  :key="option.code"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>

            <form-group horizontal name="url" :label="$t('attributes.url')">
              <b-input
                v-model="form.url"
                name="url"
                :placeholder="$t('message.please_input') + $t('attributes.url')"
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="content"
              :label="$t('attributes.content')"
            >
              <div class="content">
                <editor
                  ref="content_editor"
                  v-model="form.content"
                  image-type="News"
                ></editor>
              </div>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isUpdate ? $t('attributes.update') : $t('attributes.add') }}
          </b-button>
        </footer>

        <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import notifyService from '@/services/notifyService'
import { AREA_API } from '@/utils/apis'
import Editor from '@/components/editors/Editor4'

export default {
  name: 'TourismNewsForm',
  components: { Editor },

  props: {
    tourismNews: {
      type: Object,
      required: true
    }
  },
  data() {
    const isUpdate = !!this.tourismNews.id
    const title = isUpdate
      ? this.$t('attributes.modify')
      : this.$t('attributes.add')

    return {
      isUpdate: isUpdate,
      title: title,

      loading: false,
      loadingAreas: false,
      areas: [],
      selectedArea: null,

      sortOptions: [...Array(30).keys()].map(i => i + 1),

      form: this.extractFormValues(this.tourismNews),
      sending: false
    }
  },
  validations: {
    form: {
      topic: { required, maxLength: maxLength(150) },
      status: { required },
      area_code: { required },
      catalog_list: {},
      member_id: {},
      url: { maxLength: maxLength(1000) },
      image: { maxLength: maxLength(150) },
      show_home: {},
      honeymoon: {},
      group_code: {},
      description: { maxLength: maxLength(300) },
      customer_name: { maxLength: maxLength(50) },
      customer_tel: { maxLength: maxLength(30) },
      customer_email: { maxLength: maxLength(150) },
      source: { maxLength: maxLength(150) },
      content: {},
      start_date: {},
      end_date: {},
      sort: {},
      member_pic: {}
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.sending
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      await this.loadAreas()
      this.loading = false
    },
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.form.area_code) {
          this.selectedArea = this.areas.find(
            a => a.code === this.form.area_code
          )
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    extractFormValues(data) {
      const catalog_list = data.catalog_list
        ? data.catalog_list
            .trim()
            .split(',')
            .map(c => c.trim())
        : []

      return {
        id: data.id,
        topic: data.topic,
        status: data.status,
        area_code: data.area_code,
        catalog_list: catalog_list,
        member_id: data.member_id,
        url: data.url,
        image: data.image,
        show_home: data.show_home,
        honeymoon: data.honeymoon,
        group_code: data.group_code,
        description: data.description,
        customer_name: data.customer_name,
        customer_tel: data.customer_tel,
        customer_email: data.customer_email,
        source: data.source,
        content: data.content,
        start_date: data.start_date,
        end_date: data.end_date,
        sort: data.sort,
        member_pic: data.member_pic
      }
    },
    onSelectArea(option) {
      this.form.area_code = option && option.code
    },
    async onSend() {
      this.form.content = this.$refs.content_editor.getData()

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      const form = {
        ...this.form,
        catalog_list: this.form.catalog_list.join(',')
      }

      this.$emit('send', form)
    }
  }
}
</script>
